/**
 * Every number the candlestick page reads a pattern by, in one place.
 *
 * None of these decides WHICH bars are a pattern: that is the chart library's
 * (`detect.ts`). These are the app's own, marked as such on the page: how a
 * pattern's surroundings are scored, when the next bars are taken to have
 * agreed with it, and what the scan keeps. The Statistics pane measures what
 * the score was worth on the stored bars; it is a way to sort, not a forecast.
 *
 * Pure: no imports.
 */

/** The score of a pattern's surroundings, out of 100. Each part's most, and the range it is earned over. */
export const SCORE = {
  /** The pattern's standing (`tier` in catalog.ts). */
  tier: { 1: 5, 2: 10, 3: 15 } as Record<1 | 2 | 3, number>,
  /**
   * The move into the pattern: the close's change over `bars` bars before its
   * first candle, in average true ranges. A reversal wants a move against it
   * (a decline before a bullish one), a continuation a move with it. Nothing
   * under `from` ranges, all of it at `full`.
   */
  prior: { max: 20, bars: 10, from: 0.5, full: 3.5 },
  /**
   * Where it happened. A reversal at an extreme of the `lookback` bars before
   * it, or testing its 50- or 200-bar average and closing on the right side
   * (within `nearPct` percent), earns `level` each; outside its 20-bar,
   * two-deviation band earns `band`. A continuation earns `level` for a
   * close past everything in those bars, on its own side.
   */
  place: { max: 25, level: 15, band: 5, lookback: 20, nearPct: 1 },
  /** The last candle's volume over the average of the `avgBars` bars before it. */
  volume: { max: 10, avgBars: 50, from: 0.8, full: 2 },
  /** The pattern's height, low to high, in average true ranges. */
  size: { max: 10, from: 0.6, full: 2 },
  /**
   * The larger trend: `max` on the pattern's side of a 200-bar average that
   * has moved that way over `slopeBars` bars, nothing against one, `neither`
   * between the two.
   */
  trend: { max: 20, neither: 10, slopeBars: 20 },
  /**
   * The bullish side, where it is scored differently from the rest (the
   * owner, 2026-10-13: "fix the bullish side of the score"). Scored like a
   * bearish pattern turned over, a bullish one's score sorted nothing: over
   * twelve years of daily bars the bands were flat or upside down in every
   * era (README "The bullish side of the score"). Three things it was paid
   * for worked against it, each with the same sign in nearly every era, and
   * are paid for no longer: heavy volume, a run into the pattern, and a level
   * that earned nothing. The bearish side keeps the rules above: the same
   * three changes turned over made it worse.
   */
  bull: {
    /**
     * The move before a bullish pattern of either kind: nothing after a rise
     * of `run` ranges over the bars before it, all of it after a fall of
     * `fall`, in a straight line between. A continuation is not paid for the
     * run it continues.
     */
    prior: { run: 3.5, fall: 2 },
    /**
     * Its volume earns in the ordinary range: nothing under `from` times the
     * average, all of it from `full` to `fade`, nothing from `none` on.
     */
    volume: { from: 0.5, full: 0.7, fade: 1.3, none: 2 },
    /**
     * The places worth `place.level` to a bullish reversal: a low of the
     * bars before it, and its 50-bar average. The 200-bar average and the
     * band earn nothing, and a bullish continuation's place earns nothing.
     */
    places: ["low", "sma50"] as readonly string[],
  },
  /**
   * The bearish side, where it is scored differently from a pattern with no
   * side (the owner, 2026-10-13: "now fix the bearish side of the score").
   * Scored by the rules above its order came and went with the market: it
   * held in 2017-21, ran backwards in 2014-16 and failed at the top in
   * 2022-24 (README "The bearish side of the score"). Two parts were paid
   * for what earned nothing in any era and are changed. It is NOT the
   * bullish side turned over: heavy volume and a close under the bars before
   * have gone with a bearish pattern, and keep their points.
   */
  bear: {
    /**
     * The move before a bearish pattern of either kind is read as how far
     * the close stands ABOVE its 50-bar average, in average true ranges:
     * nothing under `from`, all of it at `full`. Over ten bars the move
     * before a bearish pattern sorted nothing; the stretch above the average
     * was followed by a lower close more often in every era measured.
     */
    stretch: { from: 3, full: 5 },
    /**
     * The places worth points to a bearish reversal: testing its 50-bar
     * average from below, and standing `stretch.full` ranges or more above
     * it (`place.level` each), and outside its band (`place.band`). A high
     * of the bars before it and the 200-bar average earn nothing. A bearish
     * continuation keeps `place.level` for a close under everything in the
     * bars before it. The stretch is counted twice on purpose, as the move
     * and as the place: it is the one thing measured that went with a
     * bearish pattern in every era, and the largest.
     */
    places: ["sma50", "band", "stretch"] as readonly string[],
  },
  /**
   * A bearish pattern on a STOCK's intraday candles (hourly, half-hourly,
   * five-minute; the regular session), where two of the bearish rules above
   * measured otherwise (the owner, 2026-10-14: "Do it for stocks only").
   * Scored by the daily rules, the bearish bands of stocks' intraday candles
   * sorted nothing on the provider's history (hours since 2017 -1.0, -1.0,
   * -0.8 points five candles on, half-hours and five minutes as flat).
   * Without these two parts they sorted: hours -1.0, -0.8, +0.8 (the top
   * band above the lowest in four eras of five), half-hours, which no rule
   * was chosen on, -1.4, -1.0, +0.8, five minutes -2.1, -1.5, +0.5. On funds
   * the same two changes sorted worse than the daily rules, so a fund's
   * candles, and every daily or longer bar, keep the rules above (README "A
   * score of its own for intraday candles").
   */
  intradayStock: {
    /** A bearish pattern's larger trend earns nothing: below a falling 200-candle average did no better than against it. */
    bearTrend: false,
    /** The places worth points to a bearish reversal: outside its band, and stretched above its average. Testing its 50-candle average earns nothing: it was followed by a lower close less often than no level, in every era of every set but one. */
    bearPlaces: ["band", "stretch"] as readonly string[],
  },
  /**
   * Candles on which a side is not scored, by interval and side, each with
   * the reason the page gives on hover (the owner, 2026-10-16: "go with your
   * recommendations", of "Don't score bearish patterns on hourly candles
   * until a rule holds there"). On production's weekly study of a year of
   * the liquid stocks' hourly candles (252 sessions), the bearish bands
   * 0-39, 40-59 and 60-79 were followed five candles on by -0.7, -1.5 and
   * -0.9 points against any candle in the same trend state: the score did
   * not sort them. On 30-minute candles (-1.3, -1.8, +1.2) and five-minute
   * ones (-2.6, +0.1, +1.8) it did, and the study of hours since 2017
   * (~/Github/afiat_marketpulse_measurements/candles-intraday-score-2026-10-13,
   * -1.0, -0.8, +0.8) had found it sorting there too: the year production
   * measured is the one a reader trades on now.
   *
   * A pattern there is still read, listed, drawn, counted and alerted on;
   * its score is null, never 0 (`scoreOf`, context.ts): it sorts last,
   * clears no score floor but none at all (`scoreClears`), and falls in no
   * band of What followed. For stocks and funds alike, whichever rules the
   * candles would otherwise be scored by (`frameOfCandles`, intraday.ts).
   * To lift one when a study finds a rule that holds there: delete its entry
   * (and its interval from `UnscoredStep` when it is the last), and raise
   * `CANDLE_SCAN_REV`.
   *
   * Weekly candles (`"1w"`, bear) since the same evening (the owner,
   * 2026-10-16: "yes", of showing weekly bearish patterns as "not scored",
   * as hourly ones are). The era study of that day
   * (~/Github/afiat_marketpulse_measurements/candles-era-study-2026-10-16,
   * `npm run candles:study -- --tf=w`, production's stored daily bars of the
   * liquid stocks since 2014 as weekly candles) found the bearish bands
   * 0-39, 40-59 and 60-79 followed five weeks on, against any week in the
   * same trend state, by +0.1, +3.4, -0.9 points in 2014-16; -1.5, -4.2,
   * -2.6 in 2017-19; +0.3, +3.9, +12.5 in 2020-21; -0.1, -3.3, -6.3 in
   * 2022-24; -1.5, +1.1, +0.6 from 2025. It sorted in one era of five and was
   * inverted in two (the higher the score, the worse). The weekly bullish
   * score sorted in four of five, and the daily bearish one in four of five,
   * so both keep their scores. A weekly pattern is read on daily rules
   * (`frameOfCandles`), so its frame is "daily:1w".
   */
  unscored: {
    "1h": {
      bear: "Not scored: on hourly candles a bearish pattern's score did not sort what followed. Over a year of the liquid stocks' hourly candles, the close five candles after a bearish pattern scoring 0-39, 40-59 and 60-79 was lower 0.7, 1.5 and 0.9 points less often than after any candle in the same trend state: a higher score did no better. On 30-minute and 5-minute candles it did. The pattern is listed and drawn without a score until a rule holds on hourly candles.",
    },
    "1w": {
      bear: "Not scored: on weekly candles a bearish pattern's score did not sort what followed. On the liquid stocks' weekly candles since 2014, read era by era, the bands 0-39, 40-59 and 60-79 rose in order in one era of five (2020-21), and in two (2017-19 and 2022-24) the higher the score the worse: in 2022-24 the close five weeks after a bearish pattern scoring 60-79 was lower 6.3 points less often than after any week in the same trend state. The bullish score sorted in four eras of five and keeps its score. The pattern is listed and drawn without a score until a rule holds on weekly candles.",
    },
  } as Readonly<Record<UnscoredStep, Readonly<Partial<Record<"bull" | "bear", string>>>>>,
  /** The average true range is of this many bars. */
  atrBars: 14,
  /** A score at or over this is called strong. */
  strong: 60,
} as const;

/** Which rules a pattern is scored by: the daily ones, or a stock's intraday candles' (`SCORE.intradayStock`). */
export type ScoreRules = "daily" | "intradayStock";

/** The intervals that have a side not scored (`SCORE.unscored`): hourly candles, and weekly ones. */
export type UnscoredStep = "1h" | "1w";

/**
 * The candles a reader reads, for `frameOfCandles`: one of the page's
 * intraday intervals, or weekly candles ("1w"). Null for daily bars and any
 * other length. The intraday ids are `IntradayStep`'s (intraday.ts), written
 * out here so this file imports nothing.
 */
export type CandleStep = "1h" | "30m" | "5m" | "1w";

/**
 * Which rules a pattern is scored by, and, on candles that have a side not
 * scored (`SCORE.unscored`), which candles they are: "intradayStock:1h" is a
 * stock's hourly candles, "daily:1w" weekly candles. Made only by
 * `frameOfCandles` for a reader of candles; the parts are read back by
 * `rulesOf` and `unscoredWhy`.
 */
export type ScoreFrame = ScoreRules | `${ScoreRules}:${UnscoredStep}`;

/** The rules a frame scores by. */
export const rulesOf = (frame: ScoreFrame): ScoreRules => (frame === "intradayStock" || frame.startsWith("intradayStock:") ? "intradayStock" : "daily");

/** Whether an interval has a side not scored. */
export const hasUnscored = (step: string | null | undefined): step is UnscoredStep => step != null && Object.prototype.hasOwnProperty.call(SCORE.unscored, step);

/**
 * The rules a symbol's candles are scored by: a STOCK's intraday candles by
 * the stocks' intraday rules (`SCORE.intradayStock`), everything else, a
 * fund's candles and every daily or longer bar, by the daily rules. Whether a
 * symbol is a stock is the universe's type (`CS`), never guessed from its
 * ticker: a symbol not known to be one is read by the daily rules.
 *
 * `step` is the interval the candles are of, when it is one of the page's
 * (`candleStepOf` for a chart's, intraday.ts): on an interval that has a side
 * not scored (`SCORE.unscored`: a bearish pattern on hourly candles, and on
 * weekly ones, for stocks and funds alike) the frame names it, and that
 * side's score is not measured. An intraday interval counts on intraday
 * candles only and weeks on daily-stamped ones only, so a step that does not
 * match the candles names nothing. The one place a reader of candles is told
 * which rules it scores by: a pane never tests an interval of its own. It is
 * here, and not in intraday.ts, so that a reader of weekly candles that must
 * not load the chart's session module (the backtest's, the Scanner's) can
 * ask it; intraday.ts hands it on to the page.
 */
export function frameOfCandles(intraday: boolean, stock: boolean | null | undefined, step: CandleStep | null): ScoreFrame {
  const rules: ScoreRules = intraday && stock === true ? "intradayStock" : "daily";
  const of = step == null ? null : (step === "1w") !== intraday ? step : null;
  return hasUnscored(of) ? `${rules}:${of}` : rules;
}

/** Why a side's score is not measured on a frame's candles, in the page's words; null where it is scored. */
export function unscoredWhy(frame: ScoreFrame, bias: "bull" | "bear" | "neutral"): string | null {
  const at = frame.indexOf(":");
  if (at < 0 || bias === "neutral") return null;
  const step = frame.slice(at + 1);
  return hasUnscored(step) ? (SCORE.unscored[step][bias] ?? null) : null;
}

/** The parts of a score, in the order they are stored and shown. */
export const SCORE_PARTS = ["tier", "prior", "place", "volume", "size", "trend"] as const;
export type ScorePart = (typeof SCORE_PARTS)[number];

export const SCORE_PART_LABEL: Record<ScorePart, string> = {
  tier: "Pattern",
  prior: "Move before",
  place: "Location",
  volume: "Volume",
  size: "Size",
  trend: "Larger trend",
};

/**
 * When the following bars are taken to have agreed. A bullish pattern is
 * confirmed by the first close above its high and failed by the first close
 * below its low, whichever comes first within `bars` bars; a bearish one the
 * other way round. With neither it has lapsed.
 */
export const CONFIRM = { bars: 3 } as const;

/**
 * A pattern taken as a trade, for the statistics: in at the close that
 * confirms it, out at its far side the moment that trades (at the open when a
 * bar opens past it), otherwise at the close `holdBars` bars later. The risk
 * is the distance between the two, and the result is counted in multiples of
 * it. Each trade is held against the SAME trade on bars with no pattern to
 * speak for them: the same stock, the same side, a stop the same percent
 * away and the same hold, entered `placebo` bars either side of it. That
 * takes the stock's drift and its volatility, and the stop's distance, out of
 * the comparison, which a comparison with "any bar and any stop" would not.
 * A result is counted between `minR` and `maxR`, so one bar of a split not
 * adjusted for cannot carry an average.
 */
export const TRADE = { holdBars: 10, placebo: [-40, -20, 20, 40], minR: -3, maxR: 10 } as const;

/** Bars ahead a pattern's outcome is measured at: the close that many bars after its last candle. */
export const HORIZONS = [1, 3, 5, 10] as const;
export type Horizon = (typeof HORIZONS)[number];

/** What the scan reads and keeps. */
export const SCAN = {
  /** Daily bars read for a session's scan: the 200-bar average, its slope, and the bars before them. */
  quickBars: 230,
  /** Of those, the newest bars the patterns are looked for in: the warm-up and the sessions kept. */
  detectBars: 112,
  /** Daily bars read for a whole build (weekly patterns and the statistics). The scanner's own window. */
  fullBars: 760,
  /** The first bar of a whole build's window the counts and the statistics are of: the 200-bar average and its slope are measured from here. */
  statsFrom: 221,
  /** Sessions of patterns kept a stock. */
  keepSessions: 5,
  /** Completed weeks of patterns kept a stock. */
  keepWeeks: 2,
  /** Candles kept a stock for the lists' small charts. */
  candles: 12,
  /** Days of market-wide counts kept. */
  historyDays: 260,
  /** The most rows one screen request is sent: the strongest, with the counts of all of them beside. */
  rowsMost: 1500,
} as const;

/** The floors of a stock worth reading a candle on: the screen's default, and the sample the statistics are measured on. */
export const LIQUID = { minPrice: 5, minAvgVolume: 200_000 } as const;
